import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import React, { createContext, ReactNode, useContext, useEffect, useReducer } from "react";
import { TutorialConfig, TutorialContextType, TutorialProgress, TutorialState } from "../types/tutorial";
import { createLogger } from "../utils/logger";

const logger = createLogger("TutorialContext");

// Tutorial actions
type TutorialAction =
  | { type: "SET_PROGRESS"; payload: TutorialProgress }
  | { type: "MARK_SEEN"; payload: string }
  | { type: "SKIP_ALL" }
  | { type: "START_TUTORIAL"; payload: string }
  | { type: "STOP_TUTORIAL" }
  | { type: "SET_ACTIVE"; payload: boolean }
  | { type: "SET_LOADING"; payload: boolean };

// Initial state
const initialState: TutorialState = {
  progress: {},
  skipAllTutorials: false,
  isActive: false,
  currentTutorial: null,
  isLoading: true,
};

// Reducer
const tutorialReducer = (state: TutorialState, action: TutorialAction): TutorialState => {
  switch (action.type) {
    case "SET_PROGRESS":
      return { ...state, progress: action.payload };
    case "MARK_SEEN":
      return {
        ...state,
        progress: {
          ...state.progress,
          [action.payload]: {
            hasSeen: true,
            completedAt: new Date(),
          },
        },
      };
    case "SKIP_ALL":
      return { ...state, skipAllTutorials: true };
    case "START_TUTORIAL":
      return { ...state, isActive: true, currentTutorial: action.payload };
    case "STOP_TUTORIAL":
      return { ...state, isActive: false, currentTutorial: null };
    case "SET_ACTIVE":
      return { ...state, isActive: action.payload };
    case "SET_LOADING":
      return { ...state, isLoading: action.payload };
    default:
      return state;
  }
};

// Create context
const TutorialContext = createContext<TutorialContextType | undefined>(undefined);

// Provider component
interface TutorialProviderProps {
  children: ReactNode;
}

export const TutorialProvider: React.FC<TutorialProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(tutorialReducer, initialState);

  // Load tutorial progress from localStorage on mount
  useEffect(() => {
    const loadTutorialProgress = () => {
      try {
        const savedProgress = localStorage.getItem("tutorialProgress");
        const skipAll = localStorage.getItem("skipAllTutorials") === "true";

        logger.info("Loading tutorial progress from localStorage:", { savedProgress, skipAll });

        if (savedProgress) {
          const progress = JSON.parse(savedProgress);
          logger.info("Loaded tutorial progress:", progress);
          dispatch({ type: "SET_PROGRESS", payload: progress });
        }

        if (skipAll) {
          logger.info("Skip all tutorials flag found in localStorage");
          dispatch({ type: "SKIP_ALL" });
        }
      } catch (error) {
        logger.error("Failed to load tutorial progress from localStorage:", error);
      } finally {
        // Always set loading to false when done
        dispatch({ type: "SET_LOADING", payload: false });
      }
    };

    loadTutorialProgress();
  }, []);

  // Save tutorial progress to localStorage
  const saveTutorialProgress = (progress: TutorialProgress, skipAll: boolean) => {
    try {
      localStorage.setItem("tutorialProgress", JSON.stringify(progress));
      localStorage.setItem("skipAllTutorials", skipAll.toString());
      logger.info("Saved tutorial progress to localStorage:", { progress, skipAll });
    } catch (error) {
      logger.error("Failed to save tutorial progress to localStorage:", error);
    }
  };

  // Check if user has seen a specific tutorial
  const hasSeenTutorial = (pageId: string): boolean => {
    const hasSeen = state.progress[pageId]?.hasSeen || false;
    return hasSeen;
  };

  // Mark tutorial as seen
  const markTutorialSeen = (pageId: string) => {
    logger.info("markTutorialSeen called for:", pageId);
    const newProgress = { ...state.progress, [pageId]: { hasSeen: true, completedAt: new Date() } };
    // Save the new progress immediately to localStorage
    saveTutorialProgress(newProgress, state.skipAllTutorials);
    // Then dispatch the state update
    dispatch({ type: "MARK_SEEN", payload: pageId });
    logger.info("Tutorial marked as seen and saved to localStorage");
  };

  // Skip all tutorials
  const skipAllTutorials = () => {
    dispatch({ type: "SKIP_ALL" });
    saveTutorialProgress(state.progress, true);
  };

  // Start tutorial
  const startTutorial = (config: TutorialConfig) => {
    logger.info("startTutorial called with config:", { id: config.id, name: config.name, stepCount: config.steps.length });

    if (state.skipAllTutorials) {
      logger.info("Tutorials are disabled, skipping tutorial:", config.id);
      return;
    }

    // Check if this tutorial is already active to prevent restart loops
    if (state.isActive && state.currentTutorial === config.id) {
      logger.info("Tutorial is already active, not starting again:", config.id);
      return;
    }

    logger.info("Starting tutorial:", config.id);
    dispatch({ type: "START_TUTORIAL", payload: config.id });

    let tutorialCompletedNormally = false;
    let isLastStep = false;

    const driverObj = driver({
      showProgress: true,
      allowClose: true, // Allow closing with escape or clicking outside
      steps: config.steps.map((step, index) => ({
        element: step.element,
        popover: {
          title: step.popover.title,
          description: step.popover.description,
          side: step.popover.side,
          align: "start",
          showButtons: index === config.steps.length - 1 ? ["previous", "next"] : ["next", "previous", "close"],
          nextBtnText: index === config.steps.length - 1 ? "Done" : "Next",
          prevBtnText: "Previous",
          closeBtnText: "Skip",
          onNextClick: (element, step) => {
            // Use the index from the map function instead of trying to find it
            const stepIndex = index;
            const isLastStep = stepIndex === config.steps.length - 1;
            const currentStep = stepIndex + 1; // Convert to 1-based indexing
            const totalSteps = config.steps.length;

            logger.info("Next button clicked:", {
              currentStep,
              totalSteps,
              stepIndex,
              isLastStep,
              shouldBeVisible: !state.skipAllTutorials && !hasSeenTutorial(config.id),
              element: element?.tagName,
              stepElement: step.element,
              tutorialId: config.id,
            });

            if (isLastStep) {
              // This is the last step, so "Next" is actually "Done"
              logger.info("Done button clicked - marking tutorial as completed normally");
              tutorialCompletedNormally = true;
              logger.info("Marking tutorial as seen immediately when Done is clicked");
              markTutorialSeen(config.id);
              // Close the tutorial dialog
              driverObj.destroy();
              // Add a small delay to ensure localStorage is updated before state change
              setTimeout(() => {
                dispatch({ type: "STOP_TUTORIAL" });
              }, 10);
              return false;
            } else {
              // Move to next step
              driverObj.moveNext();
              return false; // Prevent default behavior since we're handling it
            }
          },
          onCloseClick: (element, step) => {
            // Use the index from the map function instead of trying to find it
            const stepIndex = index;
            const currentStep = stepIndex + 1; // Convert to 1-based indexing
            const totalSteps = config.steps.length;

            logger.info("Close/Skip button clicked:", {
              currentStep,
              totalSteps,
              stepIndex,
              element: element?.tagName,
              stepElement: step.element,
              tutorialId: config.id,
            });

            logger.info("User chose to skip current tutorial from close button");
            // Only mark the current tutorial as seen, don't skip all tutorials
            markTutorialSeen(config.id);
            driverObj.destroy();
            dispatch({ type: "STOP_TUTORIAL" });

            // Prevent default close behavior since we're handling it
            return false;
          },
        },
      })),
      onHighlightStarted: (element, step) => {
        // Check if this is the last step - use the step object's position
        const stepIndex = config.steps.findIndex((s) => s.element === step.element);
        isLastStep = stepIndex === config.steps.length - 1;
        logger.info("onHighlightStarted:", { stepIndex, isLastStep, totalSteps: config.steps.length });
      },
      onDestroyStarted: () => {
        logger.info("onDestroyStarted called:", { tutorialCompletedNormally, isLastStep });
        // If tutorial was completed normally (clicked Done), mark as seen
        if (tutorialCompletedNormally && state.currentTutorial) {
          logger.info("Tutorial completed normally, marking as seen");
          markTutorialSeen(state.currentTutorial);
        }
        // Always destroy the tutorial
        driverObj.destroy();
        dispatch({ type: "STOP_TUTORIAL" });
      },
      onDestroyed: () => {
        logger.info("onDestroyed called:", { currentTutorial: state.currentTutorial, tutorialCompletedNormally });
        dispatch({ type: "STOP_TUTORIAL" });
      },
    });

    // No need to override drive method since we're using built-in Driver.js callbacks

    // Start the tutorial
    driverObj.drive();
  };

  // Stop tutorial
  const stopTutorial = () => {
    dispatch({ type: "STOP_TUTORIAL" });
  };

  // Restart tutorial (for help menu)
  const restartTutorial = (pageId: string) => {
    // This will be implemented when we have the tutorial configs
    logger.info("Restarting tutorial for page:", pageId);
  };

  const contextValue: TutorialContextType = {
    state,
    hasSeenTutorial,
    markTutorialSeen,
    skipAllTutorials,
    startTutorial,
    stopTutorial,
    restartTutorial,
  };

  return <TutorialContext.Provider value={contextValue}>{children}</TutorialContext.Provider>;
};

// Hook to use tutorial context
export const useTutorial = (): TutorialContextType => {
  const context = useContext(TutorialContext);
  if (context === undefined) {
    throw new Error("useTutorial must be used within a TutorialProvider");
  }
  return context;
};
